import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useStableId from 'src/hooks/useStableId';
import FormErrorDisplay from 'src/components/library/form/FormErrorDisplay';
import LytIcon from 'src/components/library/icon/LytIcon';
import Spinner from 'src/components/library/spinner/Spinner';
import { inputWrapperStyles, inputBaseStyles } from 'src/components/library/text-field/inputStyles';
const InputContainer = styled.div`
    display: flex;
    flex-direction: column;
`;
const InputWrapper = styled.div`
    ${inputWrapperStyles}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: ${({ $isCompact }) => ($isCompact ? '2px 8px' : '4px 8px')};
    box-sizing: border-box;
`;
const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;
const StyledInput = styled.input`
    ${inputBaseStyles}
    flex: 1;
    min-width: 0;
`;

/**
 * Accessible Input component that follows WAI-ARIA guidelines
 *
 * @param {Object} props - Component props
 * @param {string} props.id - Unique identifier for the input (required for accessibility)
 * @param {string} props.type - Input type (text, email, password, number, etc.)
 * @param {string} props.name - Name attribute for the input
 * @param {string} props.value - Current value of the input
 * @param {function} props.onChange - Handler for value changes
 * @param {string} props.label - Label text for the input (recommended for accessibility)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.helperText - Assistive text shown below the input
 * @param {string|Array<string>} props.errorText - Error message(s) for validation errors
 * @param {boolean} props.required - Whether the input is required
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {boolean} props.readOnly - Whether the input is read-only
 * @param {number} props.maxLength - Maximum character length
 * @param {string} props.describedBy - ID of an element that describes this input
 * @param {Object} props.aria - Additional ARIA attributes
 * @param {string} props.autoComplete - Hint for form autofill feature
 * @param {function} props.onFocus - Handler for focus event
 * @param {function} props.onBlur - Handler for blur event
 * @param {function} props.onKeyDown - Handler for keydown event
 * @param {string} props.leftIcon - Icon name to display on the left side
 * @param {string} props.rightIcon - Icon name to display on the right side
 * @param {boolean} props.loading - Whether to show a spinner in place of the right icon (e.g. while options are being fetched)
 * @param {boolean} props.isCompact - Whether to use compact styling (32px height to match buttons)
 */
const LytTextField = forwardRef(
    (
        {
            id = null,
            type = 'text',
            name,
            initialValue = '',
            onChange,
            label = '',
            placeholder = '',
            helperText = '',
            errorText = '',
            required = false,
            disabled = false,
            readOnly = false,
            maxLength = null,
            describedBy = '',
            aria = {},
            autoComplete = 'off',
            onFocus = () => {},
            onBlur = () => {},
            onKeyDown = () => {},
            leftIcon = null,
            rightIcon = null,
            loading = false,
            isCompact = false,
            ...rest
        },
        ref
    ) => {
        const [curValue, setCurValue] = useState(initialValue);
        // Use the imported useStableId hook to generate a stable ID
        const inputId = useStableId(`input-${name}`, id);
        const helperTextId = helperText ? `${inputId}-helper-text` : undefined;
        const errorTextId = errorText ? `${inputId}-error-text` : undefined;

        /**
         * handler for input change
         * @param {object} event html event
         */
        const handleChange = (event) => {
            const newValue = event.target.value;
            setCurValue(newValue);
            onChange(newValue);
        };

        // Combine IDs for aria-describedby
        const ariaDescribedBy =
            [describedBy, helperTextId, errorTextId]
                .filter(Boolean)
                .join(' ') || undefined;

        // Determine if the input has an error state
        const hasError = !!errorText;
        return (
            <InputContainer>
                {label && (
                    <label
                        htmlFor={inputId}
                        className={disabled ? 'disabled' : ''}>
                        {label}
                        {required && <span aria-hidden="true"> *</span>}
                    </label>
                )}

                <InputWrapper $hasError={hasError} $isCompact={isCompact}>
                    {leftIcon && (
                        <IconWrapper>
                            <LytIcon name={leftIcon} />
                        </IconWrapper>
                    )}
                    <StyledInput
                        ref={ref}
                        id={inputId}
                        type={type}
                        name={name}
                        value={curValue}
                        onChange={handleChange}
                        placeholder={placeholder}
                        disabled={disabled}
                        readOnly={readOnly}
                        required={required}
                        maxLength={maxLength}
                        aria-invalid={hasError}
                        aria-describedby={ariaDescribedBy}
                        aria-required={required}
                        autoComplete={autoComplete}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onKeyDown={onKeyDown}
                        {...aria}
                        {...rest}
                    />
                    {loading ? (
                        <IconWrapper data-testid="text-field-loading">
                            <Spinner size="small" />
                        </IconWrapper>
                    ) : (
                        rightIcon && (
                            <IconWrapper>
                                <LytIcon name={rightIcon} size={20} />
                            </IconWrapper>
                        )
                    )}
                </InputWrapper>

                {helperText && !errorText && (
                    <div id={helperTextId} className="helper-text">
                        {helperText}
                    </div>
                )}

                {errorText && (
                    <div id={errorTextId} role="alert">
                        <FormErrorDisplay error={errorText} />
                    </div>
                )}
            </InputContainer>
        );
    }
);
LytTextField.displayName = 'LytTextField';
LytTextField.propTypes = {
    id: PropTypes.string,
    type: PropTypes.string,
    name: PropTypes.string.isRequired,
    initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    errorText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]),
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    maxLength: PropTypes.number,
    describedBy: PropTypes.string,
    aria: PropTypes.object,
    autoComplete: PropTypes.string,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    leftIcon: PropTypes.string,
    rightIcon: PropTypes.string,
    loading: PropTypes.bool,
    isCompact: PropTypes.bool,
};
export default LytTextField;
